import React, { useMemo } from 'react';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Route as RouteComponent, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { Redirect, useHistory, useRoutePath } from '@shared/routing';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import MediaQuery from 'react-responsive';
import cn from 'classnames';

import { TABLET_MIN_WIDTH } from '../../utils';
import Sidebar from '../../Sidebar';
import SimpleLoader from '../../SimpleLoader';
import { PathRoute, Route, UserInfo } from '../types';
import { useConfig } from '../../context';
import LoginPage from '../../LoginPage';
import MobileSidebar from '../../MobileSidebar';
import { reachGoal, UserGoal } from '../../analytics';
import Orders from './Orders/Orders';
import { useTheme } from '../../theme';
import { TravellerProfile, useGetAccountInfoQuery } from '@websky/graphql';
import WarningModal from '../../WarningModal';
import { renderType } from '../../TravelCompanions/components/TravelCompanions';
import { useLogout } from '../../Modules/Account/hooks';
import { useHasLoyaltyProgram } from '../../hooks';
import { useCurrentUser } from '../../CurrentUser/CurrentUser.hooks';
import { useSelector } from 'react-redux';
import { getUnreadTotal } from '../store';
import { useRenderers } from '../../renderProps';
import { AUTH_DUPLICATES, sessionStorageGet } from '../../cache';
import Loader from '../../Loader';
import Helmet from 'react-helmet';

const HomePage = lazy(() => import('./HomePage'));
const SettingsPage = lazy(() => import('./Settings/Settings'));
const Logout = lazy(() => import('./Logout/Logout'));
const Loyalty = lazy(() => import('../../Loyalty'));
const TravelCompanions = lazy(() => import('../../TravelCompanions'));
const CommunicationPage = lazy(() => import('../../Communications'));

let routes: Route[] = [
	{
		title: 'My office',
		pathname: '/account',
		icon: 'Navigation/Home'
	},
	{
		title: 'Loyalty program',
		pathname: '/account/loyalty',
		icon: 'Services/Miles'
	},
	{
		title: 'Orders',
		pathname: '/account/orders',
		icon: 'Flight/Direction'
	},
	{
		title: 'Passengers',
		pathname: '/account/companions',
		icon: 'Info/Passengers'
	},
	{
		title: 'Profile',
		pathname: '/account/settings',
		icon: 'Info/Passenger'
	},
	{
		title: 'Communications',
		pathname: '/account/communications',
		icon: 'Info/Notifications',
		hasChild: true
	}
	// {
	// 	title: i18n.t('Client support'),
	// 	pathname: '/account/support',
	// 	icon: overridedIcons['Support']
	// }
];

enum LoginStages {
	LoggedIn,
	Loading,
	NotLoggedIn
}

const Account: React.FunctionComponent<RouteComponentProps> = ({ location }) => {
	const [isLoggedIn, setLoggedIn] = useState(LoginStages.Loading);
	const [accountInfoError, setAccountInfoError] = useState<string>(null);
	const hasLoyaltyProgram = useHasLoyaltyProgram();

	const communicationUnreadTotal = useSelector(getUnreadTotal);
	const history = useHistory();
	const routePath = useRoutePath();

	const { Account: css } = useTheme('AccountModule');
	const { t } = useTranslation('Account');
	const { t: checkoutT } = useTranslation('Checkout');

	const { AccountLoyalty: AccountLoyaltyOverride = Loyalty } = useRenderers();

	const currentUser = useGetAccountInfoQuery({
		variables: {
			skipOrdersList: true
		},
		fetchPolicy: 'no-cache'
	});
	const { onLogout, loading: logoutLoading } = useLogout('/account');

	const [isLoading, setLoading] = React.useState(false);

	const {
		Account: { engineURL, showCommunications },
		global: { companyInfo }
	} = useConfig();

	useCurrentUser();

	useEffect(() => {
		if (currentUser?.data?.CurrentUser?.authMethods) {
			setLoggedIn(LoginStages.LoggedIn);
		} else if (currentUser.loading) {
			setLoggedIn(LoginStages.Loading);
		} else {
			setLoggedIn(LoginStages.NotLoggedIn);
		}

		if (currentUser?.error) {
			setAccountInfoError(currentUser.error.message);
		}
	}, [currentUser]);

	const [userSettingsError, setUserSettingsError] = useState<Error>(null);

	const userInfo = useMemo(() => {
		const userValues: UserInfo = {
			email: '',
			firstName: '',
			lastName: ''
		};

		if (!currentUser?.loading && currentUser?.data?.CurrentUser) {
			currentUser.data.CurrentUser.userProfile.values.forEach(value => {
				if (value.name === 'lastName' || value.name === 'firstName' || value.name === 'email') {
					userValues[value.name] = value.value;
				}
			});
		}

		return userValues;
	}, [currentUser]);

	const refetchAccountInfo = () => {
		setLoading(true);

		currentUser.refetch().finally(() => setLoading(false));
	};

	const clearUserSettingsError = useCallback(() => {
		setUserSettingsError(null);
	}, []);

	const onLoyaltyCardSaved = () => {
		setTimeout(() => {
			refetchAccountInfo();
		}, 2000);

		return new Promise<boolean>(res => res(true));
	};

	routes = routes.filter(route => {
		const pathRoute = route as PathRoute;

		if (pathRoute.pathname === '/account/loyalty') {
			return hasLoyaltyProgram;
		}

		if (pathRoute.pathname === '/account/communications') {
			return showCommunications;
		}

		return true;
	});

	routes.find(route => {
		const pathRoute = route as PathRoute;

		if (pathRoute.pathname === '/account/loyalty') {
			route.title = checkoutT(companyInfo?.loyaltyProgramName);

			if (companyInfo?.loyalty?.redirectTo) {
				pathRoute.pathname = companyInfo?.loyalty?.redirectTo;
				pathRoute.isRedirect = true;
			}
		}

		if (pathRoute.pathname === '/account/communications') {
			pathRoute.badgeContent = communicationUnreadTotal;
		}
	});

	useEffect(() => {
		routes = [
			...routes,
			{
				title: i18n.t('Logout'),
				icon: 'Navigation/Logout',
				action: onLogout
			}
		];
	}, []);

	useEffect(() => {
		if (isLoggedIn === LoginStages.NotLoggedIn) {
			history.replace('/account');
		}
	}, [isLoggedIn]);

	const onCloseAccountInfoError = () => {
		setAccountInfoError(null);
	};

	const onGoToSettings = () => {
		history.push('/account/settings');
	};

	useEffect(() => {
		if (!!sessionStorageGet(AUTH_DUPLICATES) && currentUser?.data?.CurrentUser) {
			onGoToSettings();
		}
	}, [currentUser]);

	/**
	 * Костыль для WEBSKY-8068:
	 * На внутренних странницах раздела коммуникаций, должен быть другой сайдбар и другие оступы.
	 * Прячем стилями(opacity: 0; pointer-events: none;), чтобы сохранить отступы.
	 */
	const isInnerCommunicationsPage = useMemo(() => {
		if (showCommunications) {
			return /^\/account\/communications\/.+/.test(location.pathname);
		}

		return false;
	}, [location.pathname, showCommunications]);

	return (
		<>
			<Helmet>
				<title>
					{t('Title:companyName')} &ndash; {t('Account')}
				</title>
			</Helmet>

			{(logoutLoading || isLoggedIn === LoginStages.Loading) && <SimpleLoader />}

			{isLoggedIn === LoginStages.LoggedIn && (
				<div className={css.wrapper}>
					<MediaQuery minWidth={+TABLET_MIN_WIDTH + 1}>
						<Sidebar routes={routes} selectedRoute={location.pathname} userInfo={userInfo} />
					</MediaQuery>

					<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
						<MobileSidebar
							className={cn(css.mobileSidebar, { [css.mobileSidebar__hide]: isInnerCommunicationsPage })}
							routes={routes}
							selectedRoute={location.pathname}
						/>
					</MediaQuery>

					<div
						className={cn(css.active__route, {
							[css.active__route__communications]: isInnerCommunicationsPage
						})}
					>
						<Suspense fallback={<Loader />}>
							<Switch>
								<RouteComponent
									path={routePath('/account')}
									exact
									render={() => (
										<HomePage
											currentUser={currentUser.data.CurrentUser.userProfile as TravellerProfile}
										/>
									)}
								/>

								<RouteComponent path={routePath('/account/orders')} exact render={() => <Orders />} />

								<RouteComponent
									path={routePath('/account/profile')}
									exact
									render={() => <Redirect to="/account/companions" />}
								/>

								<RouteComponent
									path={routePath('/account/settings')}
									render={() => (
										<SettingsPage
											currentUser={currentUser?.data?.CurrentUser}
											setUserSettingsError={setUserSettingsError}
											refetchAccountInfo={refetchAccountInfo}
										/>
									)}
								/>

								<RouteComponent
									path={routePath('/account/logout')}
									exact
									render={() => {
										if (isLoggedIn === LoginStages.LoggedIn) {
											return <Logout />;
										} else if (isLoggedIn === LoginStages.NotLoggedIn) {
											return <Redirect to="/account" />;
										} else {
											return <Loader />;
										}
									}}
								/>

								{hasLoyaltyProgram && (
									<RouteComponent
										path={routePath('/account/loyalty')}
										exact
										render={() => (
											<>
												<h1 className={css.route__title}>
													{checkoutT(companyInfo?.loyaltyProgramName)}
												</h1>

												<AccountLoyaltyOverride
													isAuthorizationNeeded={
														currentUser.data?.CurrentUser?.isFfpAuthorizationNeeded
													}
													links={companyInfo?.loyalty?.links}
													ffpInfo={currentUser.data?.CurrentUser?.userFfpInfo}
													loyaltyName={checkoutT(companyInfo?.loyaltyProgramName)}
													onLoyaltyCardSaved={onLoyaltyCardSaved}
													userInfo={userInfo}
													onGoToSettings={onGoToSettings}
													isLoading={isLoading}
												/>
											</>
										)}
									/>
								)}

								<RouteComponent
									path={routePath('/account/companions')}
									exact
									render={() => (
										<>
											<TravelCompanions format={renderType.Companions} />
										</>
									)}
								/>

								{showCommunications && (
									<RouteComponent
										path={routePath('/account/communications')}
										render={() => <CommunicationPage />}
									/>
								)}

								<RouteComponent
									path={routePath('/')}
									render={() => {
										window.location.href = `${engineURL}#/404`;

										return null;
									}}
								/>
							</Switch>
						</Suspense>
					</div>
				</div>
			)}

			{isLoggedIn === LoginStages.NotLoggedIn && (
				<div className={css.loginWrapper}>
					<LoginPage
						onSuccess={isRegistration => {
							reachGoal(isRegistration ? UserGoal.Registration : UserGoal.Login);
							window.location.reload();
						}}
					/>

					<WarningModal
						isOpen={!!accountInfoError}
						onClose={onCloseAccountInfoError}
						onButtonClick={onCloseAccountInfoError}
						errorMessage={accountInfoError}
						title={t('Oops...')}
						content={t(
							'An error occurred while executing the request. Please try again later or contact a customer support service.'
						)}
						buttonText={t('Ok')}
					/>
				</div>
			)}

			<WarningModal
				title={t('Oops...')}
				content={t(
					'An error occurred while executing the request. Please try again later or contact a customer support service.'
				)}
				errorMessage={userSettingsError ? userSettingsError.message : ''}
				onClose={clearUserSettingsError}
				onButtonClick={clearUserSettingsError}
				buttonText={t('OK, thank you')}
				isOpen={!!userSettingsError}
			/>
		</>
	);
};

export default withRouter(Account);
